import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AllConcerns.css';
import { api } from '../../utils/authApi';

function AllConcerns() {
  const navigate = useNavigate();
  const [concerns, setConcerns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchAllConcerns();
  }, []);

  const fetchAllConcerns = async () => {
    try {
      setLoading(true);
      const response = await api.get('/concerns/all');
      const data = await response.json()
      // Transform the array data to objects and fetch client names
      const transformedConcerns = await Promise.all(data.Message.map(async (concern) => {
        const clientResponse = await fetch(`${process.env.REACT_APP_API_URL}/users/getClientName/${concern[1]}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        const clientData = await clientResponse.json();
        
        return {
          id: concern[0],
          clientName: clientData.Message || 'Unknown Client',
          projectId: concern[2],
          title: concern[3],
          category: concern[4],
          priority: concern[5],
          description: concern[6],
          location: concern[7],
          status: concern[8],
          engineer_note: concern[9],  
          engineer_email: concern[10],
          resolution: concern[11],
          submittedDate: concern[12],
          resolvedDate: concern[13]
        };
      }));
      
      console.log("Transformed concerns with client names:", transformedConcerns);
      setConcerns(transformedConcerns);
      setError(null);
    } catch (err) {
      setError('Failed to load concerns. Please try again.');
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredConcerns = concerns.filter(concern => {
    const matchesStatus = filterStatus === 'all' || concern.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || concern.priority === filterPriority;
    const matchesSearch = concern.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          concern.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesStatus && matchesPriority && matchesSearch;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getPriorityClass = (priority) => {
    return `priority-badge priority-${priority}`;
  };

  const getStatusClass = (status) => {
    return `status-badge status-${status.toLowerCase().replace(' ', '-')}`;
  };

  const getStatusCount = (status) => {
    return concerns.filter(c => c.status === status).length;
  };

  if (loading) {
    return (
      <div className="all-concerns-page">
        <nav className="dashboard-nav">
          <div className="container nav-content">
            <h2 className="logo">EngineeringCubes</h2>
            <button onClick={() => navigate('/engineer-dashboard')} className="btn btn-secondary">
              Back to Dashboard
            </button>
          </div>
        </nav>
        <div className="page-content">
          <div className="container">
            <div className="loading-message">Loading all concerns...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="all-concerns-page">
        <nav className="dashboard-nav">
          <div className="container nav-content">
            <h2 className="logo">EngineeringCubes</h2>
            <button onClick={() => navigate('/engineer-dashboard')} className="btn btn-secondary">
              Back to Dashboard
            </button>
          </div>
        </nav>
        <div className="page-content">
          <div className="container">
            <div className="error-message">
              {error}
              <button onClick={fetchAllConcerns} className="btn btn-primary" style={{ marginTop: '1rem' }}>
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="all-concerns-page">
      <nav className="dashboard-nav">
        <div className="container nav-content">
          <h2 className="logo">EngineeringCubes</h2>
          <button onClick={() => navigate('/engineer-dashboard')} className="btn btn-secondary">
            Back to Dashboard
          </button>
        </div>
      </nav>

      <div className="page-content">
        <div className="container">
          <div className="page-header">
            <div>
              <h1 className="page-title">All Concerns</h1>
              <p className="page-subtitle">Complete overview of all submitted concerns</p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{getStatusCount('Pending')}</div>
              <div className="stat-label">Pending</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{getStatusCount('In Progress')}</div>
              <div className="stat-label">In Progress</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{getStatusCount('Resolved')}</div>
              <div className="stat-label">Resolved</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{concerns.length}</div>
              <div className="stat-label">Total</div>
            </div>
          </div>

          {/* Filters */}
          <div className="filters-bar">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search by title or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="filter-group">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Status</option>
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Resolved">Resolved</option>
              </select>

              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Priorities</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>

          {/* Concerns Table */}
          <div className="table-container">
            {filteredConcerns.length === 0 ? (
              <div className="empty-state">
                <h3>No concerns found</h3>
                <p>Try adjusting your filters</p>
              </div>
            ) : (
              <table className="concerns-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Client Name</th>
                    <th>Project ID</th>
                    <th>Category</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th>Location</th>
                    <th>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredConcerns.map((concern) => (
                    <tr key={concern.id}>
                      <td>
                        <div className="title-cell">
                          <strong>{concern.title}</strong>
                        </div>
                      </td>
                      <td>{concern.clientName}</td>
                      <td>{concern.projectId}</td>
                      <td>
                        <span className="category-tag">{concern.category}</span>
                      </td>
                      <td>
                        <span className={getPriorityClass(concern.priority)}>
                          {concern.priority}
                        </span>
                      </td>
                      <td>
                        <span className={getStatusClass(concern.status)}>
                          {concern.status}
                        </span>
                      </td>
                      <td className="location-cell">{concern.location}</td>
                      <td className="date-cell">{formatDate(concern.submittedDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="results-count">
            Showing {filteredConcerns.length} of {concerns.length} concerns
          </div>
        </div>
      </div>
    </div>
  );
}

export default AllConcerns;